import { KrCard } from '@/common/card/KrCard';
import { Dashboard } from '@/common/dashboard/Dashboard';
import { DynamicFormField } from '@/common/form/formFields/DynamicFormField';
import { withConditionalRender } from '@/common/form/higherOrderComponents/withConditionalRender';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useRef, useState } from 'react';
import { ChbkFinGrowthCards } from './ChbkFinGrowthCards';
import { ChbkFinTrendChart } from './ChbkFinTrendChart';
import { ChbkFinTrendGraph } from './ChbkFinTrendGraph';
import { ChbkFinTrendTableCard } from './ChbkFinTrendTableCard';

const VisibleIfDay = withConditionalRender((formValues) => formValues.periodicity === 'DAY', { keepMounted: true })(DynamicFormField);
const VisibleIfMonth = withConditionalRender((formValues) => formValues.periodicity === 'MONTH', { keepMounted: true })(DynamicFormField);
const VisibleIfQuarter = withConditionalRender((formValues) => formValues.periodicity === 'QUARTER', { keepMounted: true })(DynamicFormField);

export const ChbkFinDashboard = () => {
  const rsrc = useRsrc();

  const [fieldDefs, setFieldDefs] = useState();

  const defaultFormValues = useRef({
    dateBasis: 'INV_DATE',
    monthPeriodId: null,
    qtrPeriodId: null,
    date: null,
    periodicity: 'MONTH',
    prodIds: null,
    distCustIds: null,
    ctrtTypeIds: [1, 2, 3],
    agg: 'PAID_AMT'
  });

  const [totalValues, setTotalValues] = useState();

  const ctrtTypeIdToCtrtType = { 1: 'GPO', 2: 'PHS', 3: 'FSS' };

  useEffect(() => {
    rsrc.data('sc', 'CHBK_FIN_DASHBOARD_FILTERS_DATA_PLUGIN', {}).then((res) => {
      const fieldDefsList = [
        {
          field: 'dateBasis',
          displayName: 'Date Basis',
          type: 'DROPDOWN',
          values: [
            { value: 'INV_DATE', displayName: 'Invoice Date' },
            { value: 'PYMT_DATE', displayName: 'Payment Date' }
          ],
        },
        { field: 'date', displayName: 'Date', type: 'DATE', variant: 'outlined', values: res.date },
        { field: 'monthPeriodId', displayName: 'Month Period', type: 'DROPDOWN', values: res.monthPeriods },
        { field: 'qtrPeriodId', displayName: 'Quarter Period', type: 'DROPDOWN', values: res.qtrPeriods },
        {
          field: 'periodicity',
          displayName: 'Periodicity',
          type: 'DROPDOWN',
          values: [
            { value: 'DAY', displayName: 'Day' },
            { value: 'MONTH', displayName: 'Month' },
            { value: 'QUARTER', displayName: 'Quarter' },
          ],
        },
        { field: 'prodIds', displayName: 'Products', type: 'TREE', treeData: res.prodTreeData },
        { field: 'distCustIds', displayName: 'Distributors', type: 'TREE', treeData: res.distCustTreeData },
        { field: 'ctrtTypeIds', displayName: 'Contract Types', type: 'TREE', treeData: res.ctrtTypeTreeData },
        {
          field: 'agg',
          displayName: 'Aggregate Value',
          type: 'DROPDOWN',
          values: [
            { value: 'PAID_AMT', displayName: 'Paid Amount' },
            { value: 'NET_AMT', displayName: 'Net Amount' },
            { value: 'PAID_QTY', displayName: 'Paid Quantity' },
          ],
        },
      ];

      const fieldToFieldDef = Object.fromEntries(fieldDefsList.map((fd) => [fd.field, fd]));

      fieldToFieldDef.date.component = <VisibleIfDay key="date" fieldDef={fieldToFieldDef.date} />;
      fieldToFieldDef.monthPeriodId.component = <VisibleIfMonth key="monthPeriodId" fieldDef={fieldToFieldDef.monthPeriodId} />;
      fieldToFieldDef.qtrPeriodId.component = <VisibleIfQuarter key="qtrPeriodId" fieldDef={fieldToFieldDef.qtrPeriodId} />;

      setFieldDefs(Object.values(fieldToFieldDef));

      setTotalValues({
        totalProdIds: res.prodIds,
        totalDistCustIds: res.distCustIds,
      });
      defaultFormValues.current = {
        dateBasis: 'INV_DATE',
        date: res.date,
        monthPeriodId: res.monthPeriods[0]?.value,
        qtrPeriodId: res.qtrPeriods[0]?.value,
        prodIds: res.prodIds,
        distCustIds: res.distCustIds,
        ctrtTypeIds: [1, 2, 3],
        periodicity: 'MONTH',
        agg: 'PAID_AMT',
      };
    });
  }, []);

  const formatPeriodicity = (periodicity) => {
    switch (periodicity) {
      case 'DAY':
        return 'Daily';
      case 'MONTH':
        return 'Monthly';
      case 'QUARTER':
        return 'Quarterly';
      default:
        return '';
    }
  };

  if (!fieldDefs) {
    return;
  }

  return (
    <Dashboard
      title="Chargebacks Financial Dashboard"
      titleIcon={fas.faTasks}
      fieldDefs={fieldDefs}
      defaultFormValues={defaultFormValues.current}
    >
      <SpacerLayout>
        <Spacer vertical>
          {/* Row 1 */}
          <Spacer weights={[1, 1]}>
            <KrCard title={`${formatPeriodicity(defaultFormValues.current?.periodicity)} Paid Trends`} sx={{ flex: 1 }}>
              <ChbkFinTrendChart
                totalValues={totalValues}
                ctrtTypeIdToCtrtType={ctrtTypeIdToCtrtType}
              />
            </KrCard>
            <ChbkFinTrendTableCard
              totalValues={totalValues}
              ctrtTypeIdToCtrtType={ctrtTypeIdToCtrtType}
            />
          </Spacer>

          {/* Row 2 */}
          <Spacer weights={[3, 8]}>
            <ChbkFinGrowthCards
              totalValues={totalValues}
              ctrtTypeIdToCtrtType={ctrtTypeIdToCtrtType}
            />
            <KrCard title={(defaultFormValues.current?.periodicity === 'DAY' ? 'Week over Week' : 'YoY') + ' Paid Trends'}>
              <ChbkFinTrendGraph
                totalValues={totalValues}
                ctrtTypeIdToCtrtType={ctrtTypeIdToCtrtType}
              />
            </KrCard>
          </Spacer>
        </Spacer>
      </SpacerLayout>
    </Dashboard>
  );
};
